import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { RsIcon } from '../icons'

export default function SlideCaptcha({ enabled = true, onVerified, onReset }) {
  const trackRef = useRef(null)
  const [challenge, setChallenge] = useState(null)
  const [dragging, setDragging] = useState(false)
  const [offsetX, setOffsetX] = useState(0)
  const [status, setStatus] = useState('idle') // idle | verifying | ok | fail
  const [message, setMessage] = useState('')
  const startXRef = useRef(0)
  const startOffsetRef = useRef(0)

  const loadChallenge = useCallback(async () => {
    if (!enabled) return
    setStatus('idle')
    setMessage('')
    setOffsetX(0)
    onReset?.()
    try {
      const res = await axios.post('/api/auth/captcha/challenge')
      setChallenge(res.data)
    } catch {
      setStatus('fail')
      setMessage('Failed to load captcha')
    }
  }, [enabled, onReset])

  useEffect(() => {
    loadChallenge()
  }, [loadChallenge])

  const maxOffset = challenge ? Math.max(0, challenge.trackWidth - challenge.knobWidth - 4) : 0

  const finishDrag = async (nextOffset) => {
    if (!challenge || status === 'ok') return
    setDragging(false)
    if (nextOffset < maxOffset - 2) {
      setOffsetX(0)
      setStatus('fail')
      setMessage('Please drag the slider to the end')
      window.setTimeout(() => {
        setStatus('idle')
        setMessage('')
      }, 1200)
      return
    }

    setStatus('verifying')
    try {
      const res = await axios.post('/api/auth/captcha/verify', {
        captchaId: challenge.captchaId,
        sliderX: nextOffset,
      })
      setOffsetX(maxOffset)
      setStatus('ok')
      setMessage('Verified')
      onVerified?.(res.data.captchaToken)
    } catch (err) {
      setOffsetX(0)
      setStatus('fail')
      setMessage(err.response?.data?.message || 'Verification failed')
      onReset?.()
      window.setTimeout(loadChallenge, 800)
    }
  }

  const onPointerDown = (e) => {
    if (!enabled || status === 'ok' || status === 'verifying' || !challenge) return
    e.preventDefault()
    setDragging(true)
    startXRef.current = e.clientX
    startOffsetRef.current = offsetX
  }

  useEffect(() => {
    if (!dragging) return undefined

    const onMove = (e) => {
      const delta = e.clientX - startXRef.current
      const next = Math.min(maxOffset, Math.max(0, startOffsetRef.current + delta))
      setOffsetX(next)
    }

    const onUp = (e) => {
      const delta = e.clientX - startXRef.current
      const next = Math.min(maxOffset, Math.max(0, startOffsetRef.current + delta))
      finishDrag(next)
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [dragging, maxOffset, challenge, status])

  if (!enabled) return null

  const knobWidth = challenge?.knobWidth || 44
  const trackWidth = challenge?.trackWidth || 280

  return (
    <div className="auth-slide-captcha">
      <div
        ref={trackRef}
        className={`auth-slide-track${status === 'ok' ? ' ok' : ''}${status === 'fail' ? ' fail' : ''}`}
        style={{ width: trackWidth }}
      >
        <div className="auth-slide-fill" style={{ width: offsetX + knobWidth / 2 }} />
        <span className="auth-slide-label">
          {status === 'ok' ? 'Verified' : status === 'verifying' ? 'Checking…' : 'Drag to verify'}
        </span>
        <button
          type="button"
          className="auth-slide-knob"
          style={{ width: knobWidth, transform: `translateX(${offsetX}px)` }}
          onPointerDown={onPointerDown}
          aria-label="Drag to verify"
          disabled={status === 'ok' || status === 'verifying'}
        >
          {status === 'ok' ? <RsIcon name="check" size={16} /> : <RsIcon name="chevronRight" size={16} />}
        </button>
      </div>
      <div className="auth-slide-meta">
        {message ? <span className={`auth-slide-msg ${status}`}>{message}</span> : null}
        {status === 'ok' ? (
          <button type="button" className="auth-slide-reset" onClick={loadChallenge}>
            Reset
          </button>
        ) : null}
      </div>
    </div>
  )
}
